import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { FilterConfig, StrongMASupportConfig, FibGoldenZoneConfig, DarvasBoxConfig, MATimeframeMode } from '../../types';

// MA Bounce Reversal Filter configuration type
interface MABounceReversalConfig {
  min_loss_pct: number;
  min_reversal_pct: number;
  touch_tolerance_pct: number;
  max_reversal_days: number;
  ma_selection: string[];
  require_close_above_ma: boolean;
  require_volume_confirmation: boolean;
  require_price_above_ma_before_drop: boolean;
  max_reversal_to_loss_ratio: number;
}

// MA150 V-Reversal Filter configuration type
interface MA150VReversalConfig {
  min_bearish_bars: number;
  min_v_depth_pct: number;
  max_v_depth_pct: number;
  slope_lookback: number;
  require_price_above_ma: boolean;
  require_rsi_confirmation: boolean;
  rsi_confirmation_threshold: number;
  max_recovery_pct: number;
  max_days_since_v_bottom: number;
  max_price_vs_ma_pct: number;
}

// Support Retest Filter configuration type
interface SupportRetestConfig {
  support_window_days: number;
  support_tolerance_pct: number;
  support_min_touches: number;
  min_spacing_days: number;
  break_tolerance_pct: number;
  require_rejection_wick: boolean;
  rejection_wick_ratio_min: number;
  require_volume_confirmation: boolean;
  volume_multiplier: number;
  volume_lookback_days: number;
}

// Extended FilterConfig with Golden Cross filter and Support Retest filter
interface ExtendedFilterConfig extends FilterConfig {
  goldenCrossFilterEnabled: boolean;
  // Explicitly include MA timeframe mode for consumers that rely on ExtendedFilterConfig
  // (prevents TS from losing the property through older cached/duplicated type states).
  ma_timeframe_mode: MATimeframeMode;
  active_preset_bundle?: string | null;
  // Recent MA150 cross gate (declared explicitly here so fork-ts-checker
  // picks them up even if the FilterConfig type cache is stale).
  require_recent_ma150_cross?: boolean;
  recent_ma150_cross_lookback_days?: number;
  recent_ma150_cross_bypass_pct?: number;
  // MA Bounce Reversal Filter
  ma_bounce_reversal_enabled: boolean;
  ma_bounce_reversal_strategy: 'conservative' | 'balanced' | 'aggressive' | 'custom';
  ma_bounce_reversal_config: MABounceReversalConfig;
  // MA150 V-Reversal Filter
  ma150_v_reversal_enabled: boolean;
  ma150_v_reversal_strategy: 'conservative' | 'balanced' | 'aggressive' | 'custom';
  ma150_v_reversal_config: MA150VReversalConfig;
  // Support Retest Filter (explicitly defined to fix TypeScript inference)
  support_retest_enabled: boolean;
  support_retest_strategy: 'conservative' | 'balanced' | 'aggressive' | 'custom';
  support_retest_config: SupportRetestConfig;
  // Bearish Transition Filter settings
  transition_lookback_days_bearish: number;
  transition_require_momentum_bearish: boolean;
  transition_require_volume_bearish: boolean;
  // Finviz pre-filter toggle
  use_finviz_cache: boolean;
}

// Default Strong MA Support configuration
const getDefaultStrongMASupportConfig = (): StrongMASupportConfig => ({
  enabled: false,
  moving_averages: ['MA_100', 'MA_150'],
  touch_tolerance_pct: 1.5,
  min_rebound_pct: 2.0,
  min_volume_multiplier: 1.5,
  lookback_days: 30,
  required_touches: 2,
  ma_slope_window: 5,
  min_ma_slope_pct: 0.0,
  require_higher_lows: true,
  require_rsi_confirmation: false,
  rsi_entry_min: 35,
  rsi_entry_max: 50,
  rsi_exit_min: 50,
  rsi_confirmation_window: 5,
  ma_confluence_window: 3,
  confidence_weights: {
    touch_count: 0.25,
    rebound_strength: 0.2,
    volume_support: 0.2,
    ma_slope: 0.15,
    trend_alignment: 0.1,
    confluence: 0.1,
  },
});

// Default Fibonacci Golden Zone configuration
const getDefaultFibGoldenZoneConfig = (): FibGoldenZoneConfig => ({
  lookback_days: 60,
  min_swing_move_pct: 15.0,
  volume_multiplier: 1.3,
  rsi_min: 35,
  rsi_max: 55,
});

// Default Darvas Box configuration
const getDefaultDarvasBoxConfig = (): DarvasBoxConfig => ({
  lookback_days: 250,
  min_impulse_pct: 15.0,
  max_box_atr: 2.0,
  min_touches: 2,
  containment_min_pct: 80,
});

const extendedInitialState: ExtendedFilterConfig = {
  min_market_cap: 500000000,
  min_price_target_percent: 0.10,
  // 150 MA System bundle cold-start: tight band around SMA-150
  ma_proximity_min: -0.01,
  ma_proximity_max: 0.03,
  // Recent MA150 cross gate (default-on)
  require_recent_ma150_cross: true,
  recent_ma150_cross_lookback_days: 6,
  recent_ma150_cross_bypass_pct: 0.02,
  active_preset_bundle: 'ma_150_system',
  ma_filter_mode: 'MA_150',
  ma_filter_selection: ['MA_150'],
  ma_timeframe_mode: 'DAILY' as MATimeframeMode,
  sh_avgvol: '',
  sh_relvol: '',
  sh_curvol: '',
  use_finviz_cache: false,
  trend_period_months: 6,

  price_target_mode: 'significant_high',

  standard_screening_enabled: true,
  bullish_momentum_enabled: false,

  // NEW: Enhanced trend filters (replaces old bullish momentum)
  trend_direction: 'any' as 'bullish' | 'bearish' | 'neutral' | 'any',
  trend_strength: 'any' as 'strong' | 'normal' | 'any',
  momentum_confirmation: 'any' as 'rsi' | 'stochastic' | 'any' | 'none',

  // NEW: Transition filters
  bullish_transition_enabled: false,
  bearish_transition_enabled: false,
  transition_lookback_days: 60,
  transition_require_momentum: true,
  transition_require_volume: false,
  transition_lookback_days_bearish: 60,
  transition_require_momentum_bearish: true,
  transition_require_volume_bearish: false,

  doji_filter_enabled: false,
  cup_handle_filter_enabled: false,
  head_shoulders_filter_enabled: false,
  double_pattern_filter_enabled: false,
  breakout_filter_enabled: false,
  strong_red_reversal_filter_enabled: false,
  green_doji_filter_enabled: false,
  pullback_to_support_filter_enabled: false,
  gap_down_filter_enabled: false,

  // Gap-down filter configuration
  gap_down_filter: {
    enabled: false,
    min_gap_percent: 6.0,
    require_true_gap: false,
    lookback_days: 5,
    unfilled_only: false,
  },

  // Strong MA Support filter configuration (enabled by 150 MA System bundle)
  strong_ma_support_enabled: true,
  strong_ma_support_config: getDefaultStrongMASupportConfig(),

  // New pattern-based filtering system (simplified - no scoring/weights)
  // BUG-CP-004: Changed to 'custom' to match backend default
  active_category: 'custom',
  // min_composite_score: 500,  // Disabled for simplified filtering

  // pattern_weights: { ... },  // Disabled for simplified filtering
  // scoring_thresholds: { ... },  // Disabled for simplified filtering

  // Chart analysis feature
  chart_analysis_enabled: true,

  // Instrument type filtering
  instrument_type_filter: 'ALL' as 'ALL' | 'STOCK' | 'ETF',

  // Index-based filtering
  index_filters: {
    sp500_enabled: false,
    nasdaq100_enabled: false,
    russell2000_enabled: false,
    djia_enabled: false,
  },

  // Golden Cross filter
  goldenCrossFilterEnabled: false,

  // Fibonacci Golden Zone filter
  fib_golden_zone_filter_enabled: false,
  fib_golden_zone_config: getDefaultFibGoldenZoneConfig(),

  // Volume Structure (Anchored VP) scoring confluence - per-screen opt-in (default off)
  volume_structure_enabled: false,

  // Darvas Box filter
  darvas_box_filter_enabled: false,
  darvas_box_config: getDefaultDarvasBoxConfig(),

  // Strong Support Line filter (Phase 1-3)
  strong_support_filter_enabled: false,
  strong_support_strategy: 'balanced' as 'conservative' | 'balanced' | 'aggressive' | 'custom',
  strong_support_config: {
    lookback_days: 200,
    price_tolerance_pct: 0.5,
    min_touches: 3,
    min_bounce_pct: 2.0,
    min_spacing_days: 5,
    volume_multiplier: 1.3,
    slope_min: 0.01,
    slope_max: 2.0,
    stop_loss_buffer_pct: 1.0,
    // Phase 3: Bullish Reclaim Detection (must match backend defaults)
    reclaim_window_bars: 5,
    reclaim_vol_mult: 1.3,
    reclaim_vol_mult_max: 1.5
  },

  // ============================================================
  // NEW: Extended filter defaults (Phase 4)
  // ============================================================
  volume_spike_enabled: false,
  volume_spike_strategy: 'balanced' as 'conservative' | 'balanced' | 'aggressive' | 'custom',
  volume_spike_config: {
    min_multiplier: 2.0,
    lookback_days: 20,
    min_volume_threshold: 100000,
  },

  price_action_enabled: false,
  price_action_strategy: 'balanced' as 'strict' | 'balanced' | 'moderate' | 'custom',
  price_action_config: {
    min_green_candles: 3,
    max_red_candles: 1,
    lookback_days: 5,
  },

  breakout_enabled: false,
  breakout_strategy: 'balanced' as 'strong' | 'balanced' | 'early' | 'custom',
  breakout_config: {
    resistance_lookback: 50,
    breakout_threshold: 0.02,
    volume_confirmation: true,
  },

  // RSI Recovery Filter (Buy the Dip)
  rsi_recovery_enabled: false,
  rsi_recovery_strategy: 'balanced' as 'conservative' | 'balanced' | 'aggressive' | 'custom',
  rsi_recovery_config: {
    oversold_threshold: 30,
    recovery_min: 30,
    recovery_max: 50,
    lookback_days: 10,
    rsi_period: 14,
    require_volume_confirmation: true,
  },

  // Stochastic Recovery Filter (Buy the Dip using Stochastic Oscillator)
  stochastic_enabled: false,
  stochastic_strategy: 'balanced' as 'conservative' | 'balanced' | 'aggressive' | 'custom',
  stochastic_config: {
    oversold_threshold: 20,
    recovery_min: 20,
    recovery_max: 50,
    lookback_days: 10,
    k_period: 14,
    d_period: 3,
    require_crossover: true,
    require_volume_confirmation: true,
  },

  // MA Bounce Reversal Filter (150 MA System bundle default: conservative + enabled)
  ma_bounce_reversal_enabled: true,
  ma_bounce_reversal_strategy: 'conservative' as 'conservative' | 'balanced' | 'aggressive' | 'custom',
  ma_bounce_reversal_config: {
    min_loss_pct: 4.0,
    min_reversal_pct: 0.5,
    touch_tolerance_pct: 3.0,
    max_reversal_days: 3,
    ma_selection: ['MA_50', 'MA_100', 'MA_150', 'MA_200'],
    require_close_above_ma: false,
    require_volume_confirmation: false,
    require_price_above_ma_before_drop: true,
    max_reversal_to_loss_ratio: 1.0,
  },

  // MA150 V-Reversal Filter
  ma150_v_reversal_enabled: false,
  ma150_v_reversal_strategy: 'balanced' as 'conservative' | 'balanced' | 'aggressive' | 'custom',
  ma150_v_reversal_config: {
    min_bearish_bars: 150,
    min_v_depth_pct: 5.0,
    max_v_depth_pct: 25.0,
    slope_lookback: 10,
    require_price_above_ma: true,
    require_rsi_confirmation: true,
    rsi_confirmation_threshold: 40.0,
    max_recovery_pct: 40.0,
    max_days_since_v_bottom: 30,
    max_price_vs_ma_pct: 10.0,
  },

  // Support Retest Filter (Multi-Touch Min-Price Support)
  support_retest_enabled: false,
  support_retest_strategy: 'balanced' as 'conservative' | 'balanced' | 'aggressive' | 'custom',
  support_retest_config: {
    support_window_days: 90,
    support_tolerance_pct: 0.02,
    support_min_touches: 2,
    min_spacing_days: 3,
    break_tolerance_pct: 0.01,
    require_rejection_wick: false,
    rejection_wick_ratio_min: 0.3,
    require_volume_confirmation: false,
    volume_multiplier: 1.2,
    volume_lookback_days: 20,
  },
};

// NOTE: Keep `initialState` aligned with `filtersSlice.test.ts`.
// The unit tests expect a minimal FilterConfig shape (no extended Phase-4 fields).
const initialState: any = {
  min_market_cap: 500000000,
  min_price_target_percent: 0.10,
  // 150 MA System bundle cold-start defaults
  ma_proximity_min: -0.01,
  ma_proximity_max: 0.03,
  // Recent MA150 cross gate (default-on)
  require_recent_ma150_cross: true,
  recent_ma150_cross_lookback_days: 6,
  recent_ma150_cross_bypass_pct: 0.02,
  active_preset_bundle: 'ma_150_system',
  ma_filter_mode: 'MA_150',
  ma_filter_selection: ['MA_150'],
  ma_timeframe_mode: 'DAILY' as MATimeframeMode,
  sh_avgvol: '',
  sh_relvol: '',
  sh_curvol: '',
  use_finviz_cache: false,
  trend_period_months: 6,

  price_target_mode: 'significant_high',

  standard_screening_enabled: true,
  doji_filter_enabled: false,
  cup_handle_filter_enabled: false,
  head_shoulders_filter_enabled: false,
  double_pattern_filter_enabled: false,
  breakout_filter_enabled: false,
  strong_red_reversal_filter_enabled: false,
  green_doji_filter_enabled: false,
  pullback_to_support_filter_enabled: false,
  gap_down_filter_enabled: false,

  gap_down_filter: {
    enabled: false,
    min_gap_percent: 6.0,
    require_true_gap: false,
    lookback_days: 5,
    unfilled_only: false,
  },

  strong_ma_support_enabled: true,
  strong_ma_support_config: getDefaultStrongMASupportConfig(),

  active_category: 'custom',
  chart_analysis_enabled: true,

  instrument_type_filter: 'ALL' as 'ALL' | 'STOCK' | 'ETF',

  index_filters: {
    sp500_enabled: false,
    nasdaq100_enabled: false,
    russell2000_enabled: false,
    djia_enabled: false,
  },

  goldenCrossFilterEnabled: false,

  fib_golden_zone_filter_enabled: false,
  fib_golden_zone_config: getDefaultFibGoldenZoneConfig(),

  volume_structure_enabled: false,

  darvas_box_filter_enabled: false,
  darvas_box_config: getDefaultDarvasBoxConfig(),

  strong_support_filter_enabled: false,
  strong_support_config: {
    lookback_days: 200,
    price_tolerance_pct: 0.5,
    min_touches: 3,
    min_bounce_pct: 2.0,
    min_spacing_days: 5,
    volume_multiplier: 1.3,
    slope_min: 0.01,
    slope_max: 2.0,
    stop_loss_buffer_pct: 1.0,
    reclaim_window_bars: 5,
    reclaim_vol_mult: 1.3,
    reclaim_vol_mult_max: 1.5,
  },
};

const filtersSlice = createSlice({
  name: 'filters',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<any>) => {
      // Backend is the single source of truth - use backend values directly
      const backendFilters = action.payload;

      // Map backend filter fields to frontend structure with proper defaults
      const mappedFilters: Partial<ExtendedFilterConfig> = {
        min_market_cap: backendFilters.min_market_cap ?? state.min_market_cap,
        min_price_target_percent: backendFilters.min_price_target_percent ?? state.min_price_target_percent,
        ma_proximity_min: backendFilters.ma_proximity_min ?? state.ma_proximity_min,
        ma_proximity_max: backendFilters.ma_proximity_max ?? state.ma_proximity_max,
        require_recent_ma150_cross:
          backendFilters.require_recent_ma150_cross ?? state.require_recent_ma150_cross ?? true,
        recent_ma150_cross_lookback_days:
          backendFilters.recent_ma150_cross_lookback_days ?? state.recent_ma150_cross_lookback_days ?? 6,
        recent_ma150_cross_bypass_pct:
          backendFilters.recent_ma150_cross_bypass_pct ?? state.recent_ma150_cross_bypass_pct ?? 0.02,
        active_preset_bundle:
          'active_preset_bundle' in backendFilters
            ? backendFilters.active_preset_bundle
            : state.active_preset_bundle,
        ma_filter_mode: backendFilters.ma_filter_mode ?? state.ma_filter_mode,
        ma_filter_selection: backendFilters.ma_filter_selection ?? state.ma_filter_selection,
        ma_timeframe_mode: backendFilters.ma_timeframe_mode ?? state.ma_timeframe_mode,
        sh_avgvol: backendFilters.sh_avgvol ?? state.sh_avgvol ?? '',
        sh_relvol: backendFilters.sh_relvol ?? state.sh_relvol ?? '',
        sh_curvol: backendFilters.sh_curvol ?? state.sh_curvol ?? '',
        // Never restore use_finviz_cache from DB - it's a transient per-action flag,
        // not a persistent setting. Restoring it causes normal screening to use the
        // Finviz-cached ticker subset (e.g. AMEX-only) and detect very few stocks.
        use_finviz_cache: false,
        trend_period_months: backendFilters.trend_period_months ?? state.trend_period_months,

        // Price target mode (opt-in feature)
        price_target_mode: backendFilters.price_target_mode ?? state.price_target_mode,

        // All pattern filters - use backend values as single source of truth
        standard_screening_enabled: backendFilters.standard_screening_enabled ?? state.standard_screening_enabled,
        bullish_momentum_enabled: backendFilters.bullish_momentum_enabled ?? state.bullish_momentum_enabled,
        doji_filter_enabled: backendFilters.doji_filter_enabled ?? state.doji_filter_enabled,
        cup_handle_filter_enabled: backendFilters.cup_handle_filter_enabled ?? state.cup_handle_filter_enabled,
        head_shoulders_filter_enabled: backendFilters.head_shoulders_filter_enabled ?? state.head_shoulders_filter_enabled,
        double_pattern_filter_enabled: backendFilters.double_pattern_filter_enabled ?? state.double_pattern_filter_enabled,
        breakout_filter_enabled: backendFilters.breakout_filter_enabled ?? state.breakout_filter_enabled,
        strong_red_reversal_filter_enabled: backendFilters.strong_red_reversal_filter_enabled ?? state.strong_red_reversal_filter_enabled,
        green_doji_filter_enabled: backendFilters.green_doji_filter_enabled ?? state.green_doji_filter_enabled,
        pullback_to_support_filter_enabled: backendFilters.pullback_to_support_filter_enabled ?? state.pullback_to_support_filter_enabled,
        gap_down_filter_enabled: backendFilters.gap_down_filter_enabled ?? state.gap_down_filter_enabled,

        // Gap-down filter configuration
        gap_down_filter: backendFilters.gap_down_filter ?? state.gap_down_filter,

        // Strong MA Support filter configuration
        strong_ma_support_enabled: backendFilters.strong_ma_support_enabled ?? state.strong_ma_support_enabled,
        strong_ma_support_config: backendFilters.strong_ma_support_config ?? state.strong_ma_support_config,

        // Advanced features - backend as source of truth (simplified filtering)
        active_category: backendFilters.active_category ?? state.active_category,
        // Pattern weights and scoring - map from backend when available
        min_composite_score: backendFilters.min_composite_score ?? state.min_composite_score,
        pattern_weights: backendFilters.pattern_weights ?? state.pattern_weights,
        scoring_thresholds: backendFilters.scoring_thresholds ?? state.scoring_thresholds,

        // Chart analysis feature
        chart_analysis_enabled: backendFilters.chart_analysis_enabled ?? state.chart_analysis_enabled,

        // Instrument type filtering
        instrument_type_filter: backendFilters.instrument_type_filter ?? state.instrument_type_filter,

        // Index-based filtering
        index_filters: backendFilters.index_filters ?? state.index_filters,

        // Golden Cross filter (BUG-CP-002: accept both camelCase and snake_case from backend)
        goldenCrossFilterEnabled: backendFilters.goldenCrossFilterEnabled ?? backendFilters.golden_cross_filter_enabled ?? state.goldenCrossFilterEnabled,

        // Fibonacci Golden Zone filter
        fib_golden_zone_filter_enabled: backendFilters.fib_golden_zone_filter_enabled ?? state.fib_golden_zone_filter_enabled,
        fib_golden_zone_config: backendFilters.fib_golden_zone_config ?? state.fib_golden_zone_config,

        // Volume Structure (Anchored VP) scoring confluence - per-screen opt-in
        volume_structure_enabled: backendFilters.volume_structure_enabled ?? state.volume_structure_enabled,

        // Darvas Box filter
        darvas_box_filter_enabled: backendFilters.darvas_box_filter_enabled ?? state.darvas_box_filter_enabled,
        darvas_box_config: backendFilters.darvas_box_config ?? state.darvas_box_config,

        // Strong Support Line filter (Phase 1)
        strong_support_filter_enabled: backendFilters.strong_support_filter_enabled ?? state.strong_support_filter_enabled,
        strong_support_strategy: backendFilters.strong_support_strategy ?? state.strong_support_strategy,
        strong_support_config: backendFilters.strong_support_config ?? state.strong_support_config,

        // ============================================================
        // NEW: Extended filter configurations (Phase 4)
        // ============================================================
        volume_spike_enabled: backendFilters.volume_spike_enabled ?? state.volume_spike_enabled,
        volume_spike_strategy: backendFilters.volume_spike_strategy ?? state.volume_spike_strategy,
        volume_spike_config: backendFilters.volume_spike_config ?? state.volume_spike_config,
        price_action_enabled: backendFilters.price_action_enabled ?? state.price_action_enabled,
        price_action_strategy: backendFilters.price_action_strategy ?? state.price_action_strategy,
        price_action_config: backendFilters.price_action_config ?? state.price_action_config,
        // BUG-CP-003: Accept both breakout_enabled and breakout_extended_enabled
        breakout_enabled: backendFilters.breakout_enabled ?? backendFilters.breakout_extended_enabled ?? state.breakout_enabled,
        breakout_strategy: backendFilters.breakout_strategy ?? state.breakout_strategy,
        breakout_config: backendFilters.breakout_config ?? state.breakout_config,

        // RSI Recovery Filter
        rsi_recovery_enabled: backendFilters.rsi_recovery_enabled ?? state.rsi_recovery_enabled,
        rsi_recovery_strategy: backendFilters.rsi_recovery_strategy ?? state.rsi_recovery_strategy,
        rsi_recovery_config: backendFilters.rsi_recovery_config ?? state.rsi_recovery_config,

        // Stochastic Recovery Filter
        stochastic_enabled: backendFilters.stochastic_enabled ?? state.stochastic_enabled,
        stochastic_strategy: backendFilters.stochastic_strategy ?? state.stochastic_strategy,
        stochastic_config: backendFilters.stochastic_config ?? state.stochastic_config,

        // MA Bounce Reversal Filter
        ma_bounce_reversal_enabled: backendFilters.ma_bounce_reversal_enabled ?? state.ma_bounce_reversal_enabled,
        ma_bounce_reversal_strategy: backendFilters.ma_bounce_reversal_strategy ?? state.ma_bounce_reversal_strategy,
        ma_bounce_reversal_config: backendFilters.ma_bounce_reversal_config ?? state.ma_bounce_reversal_config,

        // MA150 V-Reversal Filter
        ma150_v_reversal_enabled: backendFilters.ma150_v_reversal_enabled ?? state.ma150_v_reversal_enabled,
        ma150_v_reversal_strategy: backendFilters.ma150_v_reversal_strategy ?? state.ma150_v_reversal_strategy,
        ma150_v_reversal_config: backendFilters.ma150_v_reversal_config ?? state.ma150_v_reversal_config,

        // Support Retest Filter
        support_retest_enabled: backendFilters.support_retest_enabled ?? state.support_retest_enabled,
        support_retest_strategy: backendFilters.support_retest_strategy ?? state.support_retest_strategy,
        support_retest_config: backendFilters.support_retest_config ?? state.support_retest_config,
      };

      // Preserve all Finviz keys loaded from DB so dialog reopens with saved values.
      Object.entries(backendFilters).forEach(([key, value]) => {
        if (key.startsWith('finviz_')) {
          (mappedFilters as Record<string, any>)[key] = value;
        }
      });

      return { ...state, ...mappedFilters };
    },
    updateFilter: (state, action: PayloadAction<{ key: keyof ExtendedFilterConfig; value: any }>) => {
      const { key, value } = action.payload;
      (state as any)[key] = value;
    },
    updateStrongMASupportConfig: (state, action: PayloadAction<Partial<StrongMASupportConfig>>) => {
      if (state.strong_ma_support_config) {
        state.strong_ma_support_config = {
          ...state.strong_ma_support_config,
          ...action.payload,
        };
      } else {
        state.strong_ma_support_config = {
          ...getDefaultStrongMASupportConfig(),
          ...action.payload,
        };
      }
    },
    setGoldenCrossFilter: (state, action: PayloadAction<boolean>) => {
      state.goldenCrossFilterEnabled = action.payload;
      console.log(`Golden Cross filter ${action.payload ? 'enabled' : 'disabled'}`);
    },
    updateFibGoldenZoneConfig: (state, action: PayloadAction<Partial<FibGoldenZoneConfig>>) => {
      if (state.fib_golden_zone_config) {
        state.fib_golden_zone_config = {
          ...state.fib_golden_zone_config,
          ...action.payload,
        };
      } else {
        state.fib_golden_zone_config = {
          ...getDefaultFibGoldenZoneConfig(),
          ...action.payload,
        };
      }
    },
    setFibGoldenZoneFilter: (state, action: PayloadAction<boolean>) => {
      state.fib_golden_zone_filter_enabled = action.payload;
      console.log(`Fibonacci Golden Zone filter ${action.payload ? 'enabled' : 'disabled'}`);
    },
    updateDarvasBoxConfig: (state, action: PayloadAction<Partial<DarvasBoxConfig>>) => {
      if (state.darvas_box_config) {
        state.darvas_box_config = {
          ...state.darvas_box_config,
          ...action.payload,
        };
      } else {
        state.darvas_box_config = {
          ...getDefaultDarvasBoxConfig(),
          ...action.payload,
        };
      }
    },
    setDarvasBoxFilter: (state, action: PayloadAction<boolean>) => {
      state.darvas_box_filter_enabled = action.payload;
      console.log(`Darvas Box filter ${action.payload ? 'enabled' : 'disabled'}`);
    },
    // ============================================================
    // NEW: Extended filter reducers (Phase 4)
    // ============================================================
    setVolumeSpikeEnabled: (state, action: PayloadAction<boolean>) => {
      state.volume_spike_enabled = action.payload;
    },
    setVolumeSpikeConfig: (state, action: PayloadAction<Partial<NonNullable<typeof extendedInitialState.volume_spike_config>>>) => {
      const defaults = extendedInitialState.volume_spike_config as NonNullable<typeof extendedInitialState.volume_spike_config>;
      const current = state.volume_spike_config;
      state.volume_spike_config = {
        min_multiplier: action.payload?.min_multiplier ?? current?.min_multiplier ?? defaults.min_multiplier,
        lookback_days: action.payload?.lookback_days ?? current?.lookback_days ?? defaults.lookback_days,
        min_volume_threshold: action.payload?.min_volume_threshold ?? current?.min_volume_threshold ?? defaults.min_volume_threshold,
      };
    },
    setPriceActionEnabled: (state, action: PayloadAction<boolean>) => {
      state.price_action_enabled = action.payload;
    },
    setPriceActionConfig: (state, action: PayloadAction<Partial<NonNullable<typeof extendedInitialState.price_action_config>>>) => {
      const defaults = extendedInitialState.price_action_config as NonNullable<typeof extendedInitialState.price_action_config>;
      const current = state.price_action_config;
      state.price_action_config = {
        min_green_candles: action.payload?.min_green_candles ?? current?.min_green_candles ?? defaults.min_green_candles,
        max_red_candles: action.payload?.max_red_candles ?? current?.max_red_candles ?? defaults.max_red_candles,
        lookback_days: action.payload?.lookback_days ?? current?.lookback_days ?? defaults.lookback_days,
      };
    },
    setBreakoutEnabled: (state, action: PayloadAction<boolean>) => {
      state.breakout_enabled = action.payload;
    },
    setBreakoutConfig: (state, action: PayloadAction<Partial<NonNullable<typeof extendedInitialState.breakout_config>>>) => {
      const defaults = extendedInitialState.breakout_config as NonNullable<typeof extendedInitialState.breakout_config>;
      const current = state.breakout_config;
      state.breakout_config = {
        resistance_lookback: action.payload?.resistance_lookback ?? current?.resistance_lookback ?? defaults.resistance_lookback,
        breakout_threshold: action.payload?.breakout_threshold ?? current?.breakout_threshold ?? defaults.breakout_threshold,
        volume_confirmation: action.payload?.volume_confirmation ?? current?.volume_confirmation ?? defaults.volume_confirmation,
      };
    },
    setRsiRecoveryEnabled: (state, action: PayloadAction<boolean>) => {
      state.rsi_recovery_enabled = action.payload;
    },
    setRsiRecoveryConfig: (state, action: PayloadAction<Partial<NonNullable<typeof extendedInitialState.rsi_recovery_config>>>) => {
      const defaults = extendedInitialState.rsi_recovery_config as NonNullable<typeof extendedInitialState.rsi_recovery_config>;
      const current = state.rsi_recovery_config;
      state.rsi_recovery_config = {
        oversold_threshold: action.payload?.oversold_threshold ?? current?.oversold_threshold ?? defaults.oversold_threshold,
        recovery_min: action.payload?.recovery_min ?? current?.recovery_min ?? defaults.recovery_min,
        recovery_max: action.payload?.recovery_max ?? current?.recovery_max ?? defaults.recovery_max,
        lookback_days: action.payload?.lookback_days ?? current?.lookback_days ?? defaults.lookback_days,
        rsi_period: action.payload?.rsi_period ?? current?.rsi_period ?? defaults.rsi_period,
        require_volume_confirmation: action.payload?.require_volume_confirmation ?? current?.require_volume_confirmation ?? defaults.require_volume_confirmation,
      };
    },
    // Stochastic Recovery Filter actions
    setStochasticEnabled: (state, action: PayloadAction<boolean>) => {
      state.stochastic_enabled = action.payload;
    },
    setStochasticConfig: (state, action: PayloadAction<Partial<NonNullable<typeof extendedInitialState.stochastic_config>>>) => {
      const defaults = extendedInitialState.stochastic_config as NonNullable<typeof extendedInitialState.stochastic_config>;
      const current = state.stochastic_config;
      state.stochastic_config = {
        oversold_threshold: action.payload?.oversold_threshold ?? current?.oversold_threshold ?? defaults.oversold_threshold,
        recovery_min: action.payload?.recovery_min ?? current?.recovery_min ?? defaults.recovery_min,
        recovery_max: action.payload?.recovery_max ?? current?.recovery_max ?? defaults.recovery_max,
        lookback_days: action.payload?.lookback_days ?? current?.lookback_days ?? defaults.lookback_days,
        k_period: action.payload?.k_period ?? current?.k_period ?? defaults.k_period,
        d_period: action.payload?.d_period ?? current?.d_period ?? defaults.d_period,
        require_crossover: action.payload?.require_crossover ?? current?.require_crossover ?? defaults.require_crossover,
        require_volume_confirmation: action.payload?.require_volume_confirmation ?? current?.require_volume_confirmation ?? defaults.require_volume_confirmation,
      };
    },
    // MA Bounce Reversal Filter actions
    setMABounceReversalEnabled: (state, action: PayloadAction<boolean>) => {
      state.ma_bounce_reversal_enabled = action.payload;
    },
    setMABounceReversalStrategy: (state, action: PayloadAction<'conservative' | 'balanced' | 'aggressive' | 'custom'>) => {
      state.ma_bounce_reversal_strategy = action.payload;
    },
    setMABounceReversalConfig: (state, action: PayloadAction<Partial<NonNullable<typeof extendedInitialState.ma_bounce_reversal_config>>>) => {
      const defaults = extendedInitialState.ma_bounce_reversal_config as NonNullable<typeof extendedInitialState.ma_bounce_reversal_config>;
      const current = state.ma_bounce_reversal_config;
      state.ma_bounce_reversal_config = {
        min_loss_pct: action.payload?.min_loss_pct ?? current?.min_loss_pct ?? defaults.min_loss_pct,
        min_reversal_pct: action.payload?.min_reversal_pct ?? current?.min_reversal_pct ?? defaults.min_reversal_pct,
        touch_tolerance_pct: action.payload?.touch_tolerance_pct ?? current?.touch_tolerance_pct ?? defaults.touch_tolerance_pct,
        max_reversal_days: action.payload?.max_reversal_days ?? current?.max_reversal_days ?? defaults.max_reversal_days,
        ma_selection: action.payload?.ma_selection ?? current?.ma_selection ?? defaults.ma_selection,
        require_close_above_ma: action.payload?.require_close_above_ma ?? current?.require_close_above_ma ?? defaults.require_close_above_ma,
        require_volume_confirmation: action.payload?.require_volume_confirmation ?? current?.require_volume_confirmation ?? defaults.require_volume_confirmation,
        require_price_above_ma_before_drop: action.payload?.require_price_above_ma_before_drop ?? current?.require_price_above_ma_before_drop ?? defaults.require_price_above_ma_before_drop,
        max_reversal_to_loss_ratio: action.payload?.max_reversal_to_loss_ratio ?? current?.max_reversal_to_loss_ratio ?? defaults.max_reversal_to_loss_ratio,
      };
    },
    // MA150 V-Reversal Filter actions
    setMA150VReversalEnabled: (state, action: PayloadAction<boolean>) => {
      state.ma150_v_reversal_enabled = action.payload;
    },
    setMA150VReversalStrategy: (state, action: PayloadAction<'conservative' | 'balanced' | 'aggressive' | 'custom'>) => {
      state.ma150_v_reversal_strategy = action.payload;
    },
    setMA150VReversalConfig: (state, action: PayloadAction<Partial<NonNullable<typeof extendedInitialState.ma150_v_reversal_config>>>) => {
      const defaults = extendedInitialState.ma150_v_reversal_config as NonNullable<typeof extendedInitialState.ma150_v_reversal_config>;
      const current = state.ma150_v_reversal_config;
      state.ma150_v_reversal_config = {
        min_bearish_bars: action.payload?.min_bearish_bars ?? current?.min_bearish_bars ?? defaults.min_bearish_bars,
        min_v_depth_pct: action.payload?.min_v_depth_pct ?? current?.min_v_depth_pct ?? defaults.min_v_depth_pct,
        max_v_depth_pct: action.payload?.max_v_depth_pct ?? current?.max_v_depth_pct ?? defaults.max_v_depth_pct,
        slope_lookback: action.payload?.slope_lookback ?? current?.slope_lookback ?? defaults.slope_lookback,
        require_price_above_ma: action.payload?.require_price_above_ma ?? current?.require_price_above_ma ?? defaults.require_price_above_ma,
        require_rsi_confirmation: action.payload?.require_rsi_confirmation ?? current?.require_rsi_confirmation ?? defaults.require_rsi_confirmation,
        rsi_confirmation_threshold: action.payload?.rsi_confirmation_threshold ?? current?.rsi_confirmation_threshold ?? defaults.rsi_confirmation_threshold,
        max_recovery_pct: action.payload?.max_recovery_pct ?? current?.max_recovery_pct ?? defaults.max_recovery_pct,
        max_days_since_v_bottom: action.payload?.max_days_since_v_bottom ?? current?.max_days_since_v_bottom ?? defaults.max_days_since_v_bottom,
        max_price_vs_ma_pct: action.payload?.max_price_vs_ma_pct ?? current?.max_price_vs_ma_pct ?? defaults.max_price_vs_ma_pct,
      };
    },
    // Support Retest Filter actions
    setSupportRetestEnabled: (state, action: PayloadAction<boolean>) => {
      state.support_retest_enabled = action.payload;
    },
    setSupportRetestStrategy: (state, action: PayloadAction<'conservative' | 'balanced' | 'aggressive' | 'custom'>) => {
      state.support_retest_strategy = action.payload;
    },
    setSupportRetestConfig: (state, action: PayloadAction<Partial<NonNullable<typeof extendedInitialState.support_retest_config>>>) => {
      const defaults = extendedInitialState.support_retest_config as NonNullable<typeof extendedInitialState.support_retest_config>;
      const current = state.support_retest_config;
      state.support_retest_config = {
        support_window_days: action.payload?.support_window_days ?? current?.support_window_days ?? defaults.support_window_days,
        support_tolerance_pct: action.payload?.support_tolerance_pct ?? current?.support_tolerance_pct ?? defaults.support_tolerance_pct,
        support_min_touches: action.payload?.support_min_touches ?? current?.support_min_touches ?? defaults.support_min_touches,
        min_spacing_days: action.payload?.min_spacing_days ?? current?.min_spacing_days ?? defaults.min_spacing_days,
        break_tolerance_pct: action.payload?.break_tolerance_pct ?? current?.break_tolerance_pct ?? defaults.break_tolerance_pct,
        require_rejection_wick: action.payload?.require_rejection_wick ?? current?.require_rejection_wick ?? defaults.require_rejection_wick,
        rejection_wick_ratio_min: action.payload?.rejection_wick_ratio_min ?? current?.rejection_wick_ratio_min ?? defaults.rejection_wick_ratio_min,
        require_volume_confirmation: action.payload?.require_volume_confirmation ?? current?.require_volume_confirmation ?? defaults.require_volume_confirmation,
        volume_multiplier: action.payload?.volume_multiplier ?? current?.volume_multiplier ?? defaults.volume_multiplier,
        volume_lookback_days: action.payload?.volume_lookback_days ?? current?.volume_lookback_days ?? defaults.volume_lookback_days,
      };
    },
    resetFilters: () => initialState,
  },
});

export const {
  setFilters,
  updateFilter,
  updateStrongMASupportConfig,
  setGoldenCrossFilter,
  updateFibGoldenZoneConfig,
  setFibGoldenZoneFilter,
  updateDarvasBoxConfig,
  setDarvasBoxFilter,
  // NEW: Extended filter actions (Phase 4)
  setVolumeSpikeEnabled,
  setVolumeSpikeConfig,
  setPriceActionEnabled,
  setPriceActionConfig,
  setBreakoutEnabled,
  setBreakoutConfig,
  // RSI Recovery Filter actions
  setRsiRecoveryEnabled,
  setRsiRecoveryConfig,
  // Stochastic Recovery Filter actions
  setStochasticEnabled,
  setStochasticConfig,
  // MA Bounce Reversal Filter actions
  setMABounceReversalEnabled,
  setMABounceReversalStrategy,
  setMABounceReversalConfig,
  // MA150 V-Reversal Filter actions
  setMA150VReversalEnabled,
  setMA150VReversalStrategy,
  setMA150VReversalConfig,
  // Support Retest Filter actions
  setSupportRetestEnabled,
  setSupportRetestStrategy,
  setSupportRetestConfig,
  resetFilters
} = filtersSlice.actions;
export const getInitialFiltersState = () => initialState;
// Export ExtendedFilterConfig type for use in components
export type { ExtendedFilterConfig };
export default filtersSlice.reducer;
